import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  pluck,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { patchSelectedCandidateAssociationFilterEffect } from '@collections/associations';
import { candidatesSelect } from '@collections/candidates';
import {
  CandidatesPageFilter,
  CandidatesPageFilterActionTypes,
} from '@modules/candidates/+filter/store';
import {
  CandidatePageFilter,
  getCandidatePageKey,
} from '@shared/candidate-filters/candidate-filters.model';
import {
  associationDefaultFilter,
  AssociationFilter,
  Candidate,
  isCountry,
  isState,
  Location,
} from '@shared/models';
import { JobFeaturedCandidate } from '@shared/models/job-featured-candidate';
import { Pagination } from '@shared/pagination';
import { JobMatchCandidatesService, SearchCandidatesService } from '@shared/services';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { isTruthy } from '@shared/utils';

import { DEFAULT_LOCATION_RADIUS_IN_MILES } from '../+filter/store';
import {
  candidatesJobsList,
  candidatesJobsLoad,
  candidatesJobsLoadFail,
  candidatesJobsLoadSuccess,
  candidatesLoad,
  candidatesLoadFail,
  candidatesLoadSuccess,
  candidatesPageList,
  candidatesSimilarClear,
  candidatesSimilarList,
  candidatesSimilarLoad,
  candidatesSimilarLoadSuccess,
} from './candidates.actions';
import { getCandidatesPageFilter } from './candidates.reducer';
import { featureFlagsIsActive } from 'mojo-feature-flags';

@Injectable()
export class CandidatesEffects {
  filtersApplied$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CandidatesPageFilterActionTypes.Set, CandidatesPageFilterActionTypes.Clear),
      map(() => candidatesLoad({})),
    ),
  );

  loadSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesLoadSuccess),
      pluck('payload'),
      map((payload: Pagination<Candidate>) => candidatesPageList({ payload })),
    ),
  );

  load$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesLoad),
      pluck('payload'),
      withLatestFrom(this.store.pipe(select(getCandidatesPageFilter))),
      switchMap(([payload, filters]: [any, CandidatesPageFilter]) => {
        const payloadParams = !!payload ? { ...payload.params } : {};
        const params = {
          ...payloadParams,
          sort: 'lastModifiedDate',
          size: 10,
        };

        const locationMilesRadius =
          !filters.locations ||
          filters.locations.some((location: Location) => isCountry(location) || isState(location))
            ? DEFAULT_LOCATION_RADIUS_IN_MILES
            : filters.locationMilesRadius;

        const body = {
          ...filters,
          locationMilesRadius,
          resumeUploadDate: filters.resumeUploadDate?.value,
        };

        return this.candidateSearchCandidatesService.post({ ...payload, params, body }).pipe(
          map((response) => candidatesLoadSuccess({ payload: response })),
          catchError((error) => of(candidatesLoadFail({ error }))),
        );
      }),
    ),
  );

  loadSimilarSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesSimilarLoadSuccess),
      pluck('payload'),
      map((payload: Pagination<Candidate>) => candidatesSimilarList({ payload })),
    ),
  );

  loadSimilar$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesSimilarLoad),
      pluck('payload'),
      switchMap((id) => {
        const serviceCall = this.candidateSearchCandidatesService.similar(id);
        return serviceCall.pipe(
          map((response) => candidatesSimilarLoadSuccess({ payload: response })),
          catchError((error) => of(candidatesLoadFail({ error }))),
        );
      }),
    ),
  );

  triggerLoadSimilar$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesSelect),
      pluck('id'),
      filter(isTruthy),
      mergeMap((id: number) => [candidatesSimilarClear(), candidatesSimilarLoad({ payload: id })]),
    ),
  );

  loadCandidatesJobsSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesJobsLoadSuccess),
      pluck('payload'),
      map((payload) => candidatesJobsList({ payload })),
    ),
  );

  loadAssociationsForSelectedCandidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesSelect),
      filter(({ id = 0 }) => id !== 0),
      map(({ id }) => {
        const key = getCandidatePageKey(id);
        const currentFilter = this.localStorage.getSavedState<CandidatePageFilter>(key);
        let newFilter: AssociationFilter;
        if (!currentFilter) {
          newFilter = associationDefaultFilter;
        }
        return { filter: newFilter, id: key, assigned: false };
      }),
      map((params) => patchSelectedCandidateAssociationFilterEffect(params)),
    ),
  );

  loadJobs$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesJobsLoad),
      pluck('payload'),
      switchMap((id: number) =>
        this.jobMatchService.get({ id }).pipe(
          map((response) => response.jobs),
          map((jobs: JobFeaturedCandidate[]) => candidatesJobsLoadSuccess({ payload: jobs })),
          catchError((error) => of(candidatesJobsLoadFail({ error }))),
        ),
      ),
    ),
  );

  triggerLoadJobs$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesSelect),
      pluck('id'),
      filter(isTruthy),
      map((id: number) => candidatesJobsLoad({ payload: id })),
    ),
  );

  constructor(
    private actions$: Actions,
    private candidateSearchCandidatesService: SearchCandidatesService,
    private jobMatchService: JobMatchCandidatesService,
    private store: Store<{}>,
    private localStorage: LocalStorageService,
  ) {}
}
